import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  address: string;
  status: string;
  assigned_agent: string;
  configuration: string;
  comments: string;
  rdv_date?: string;
  rdv_time?: string;
  rdv_notes?: string;
  created_at: string;
  updated_at: string;
  assigned_user?: {
    id: string;
    name: string;
    email: string;
  };
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
}

interface PlanningProps {
  currentUser: {
    id: string;
    name: string;
    role: 'admin' | 'agent';
  };
}

export default function Planning({ currentUser }: PlanningProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('day');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [editingRdv, setEditingRdv] = useState<Client | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Charger les données depuis Supabase
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger les clients avec RDV
      const clientsResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=list`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });
      const clientsResult = await clientsResponse.json();
      
      // Charger les utilisateurs
      const usersResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management?action=list`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });
      const usersResult = await usersResponse.json();

      if (clientsResult.success) setClients(clientsResult.clients || []);
      if (usersResult.success) setUsers(usersResult.users || []);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Nouvelle fonction de chargement des clients avec prise en compte de l'entreprise
  const loadClients = async () => {
    try {
      setLoading(true);
      
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (!currentUser.company_id) {
        setError('Société non trouvée');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=list&company_id=${currentUser.company_id}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        }
      });
      const result = await response.json();

      if (result.success) setClients(result.clients || []);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de mise à jour d'un client
  const updateClient = async (clientData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientData)
      });
      // Vous pourriez gérer la réponse ici si nécessaire
    } catch (error) {
      console.error('Erreur lors de la mise à jour du client:', error);
    }
  };

  useEffect(() => {
    loadData();
    
    // Actualiser les données toutes les 30 secondes
    const interval = setInterval(loadData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Filtrer les clients avec RDV selon le rôle et les filtres
  const clientsWithRdv = clients.filter(client => 
    client.status === 'rdv-fixe' && 
    client.rdv_date && 
    (currentUser.role === 'admin' || client.assigned_agent === currentUser.id) &&
    (selectedAgent === '' || client.assigned_agent === selectedAgent)
  );

  // Filtrer par date sélectionnée
  const rdvForSelectedDate = clientsWithRdv.filter(client => 
    client.rdv_date === selectedDate
  );

  // RDV du jour
  const today = new Date().toISOString().split('T')[0];
  const rdvToday = clientsWithRdv.filter(client => 
    client.rdv_date === today
  );

  // Prochains RDV (7 prochains jours)
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  const upcomingRdv = clientsWithRdv.filter(client => {
    const rdvDate = new Date(client.rdv_date!);
    const todayDate = new Date(today);
    return rdvDate > todayDate && rdvDate <= nextWeek;
  }).sort((a, b) => {
    const dateA = new Date(`${a.rdv_date}T${a.rdv_time || '00:00'}`);
    const dateB = new Date(`${b.rdv_date}T${b.rdv_time || '00:00'}`);
    return dateA.getTime() - dateB.getTime();
  });

  // Obtenir les créneaux horaires disponibles
  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  // Formater la date pour l'affichage
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Formater l'heure
  const formatTime = (timeString: string) => {
    return timeString || 'Heure non définie';
  };

  if (loading && clients.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Chargement du planning...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planning des rendez-vous</h1>
          <div className="flex items-center space-x-2">
            <p className="text-gray-600">{rdvToday.length} RDV aujourd'hui</p>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
              <span className="text-xs text-green-600">Temps réel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Message d'information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <i className="ri-calendar-check-line text-blue-600 mr-2"></i>
          <div>
            <p className="text-blue-800 text-sm font-semibold">
              📅 Planning synchronisé avec Supabase !
            </p>
            <p className="text-blue-700 text-xs">
              Tous les rendez-vous sont automatiquement synchronisés en temps réel
            </p>
          </div>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Aujourd'hui</p>
              <p className="text-2xl font-bold">{rdvToday.length}</p>
            </div>
            <i className="ri-calendar-today-line text-2xl text-blue-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Cette semaine</p>
              <p className="text-2xl font-bold">{upcomingRdv.length}</p>
            </div>
            <i className="ri-calendar-week-line text-2xl text-green-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total RDV</p>
              <p className="text-2xl font-bold">{clientsWithRdv.length}</p>
            </div>
            <i className="ri-calendar-line text-2xl text-purple-200"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Date sélectionnée</p>
              <p className="text-2xl font-bold">{rdvForSelectedDate.length}</p>
            </div>
            <i className="ri-calendar-check-line text-2xl text-orange-200"></i>
          </div>
        </div>
      </div>

      {/* Filtres et navigation */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date sélectionnée
            </label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

          {currentUser.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filtrer par agent
              </label>
              <Select
                options={[
                  { value: '', label: 'Tous les agents' },
                  ...users
                    .filter(user => user.role === 'agent' && user.active)
                    .map(user => ({
                      value: user.id,
                      label: user.name
                    }))
                ]}
                value={selectedAgent}
                onChange={setSelectedAgent}
                placeholder="Sélectionner un agent"
              />
            </div>
          )}

          <div className="flex items-end">
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedDate(today);
                setSelectedAgent('');
              }}
            >
              <i className="ri-refresh-line mr-2"></i>
              Aujourd'hui
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* RDV du jour */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                RDV aujourd'hui ({rdvToday.length})
              </h2>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2"></div>
                <span className="text-xs text-red-600">Priorité</span>
              </div>
            </div>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {rdvToday.length > 0 ? (
              <div className="space-y-3">
                {rdvToday
                  .sort((a, b) => (a.rdv_time || '').localeCompare(b.rdv_time || ''))
                  .map(client => {
                    const agent = users.find(u => u.id === client.assigned_agent) || client.assigned_user;
                    return (
                      <div key={client.id} className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {client.first_name} {client.last_name}
                            </p>
                            <p className="text-sm text-gray-600">{client.phone}</p>
                            <div className="flex items-center mt-2">
                              <i className="ri-time-line text-red-600 mr-1"></i>
                              <span className="text-sm font-medium text-red-600">
                                {formatTime(client.rdv_time || '')}
                              </span>
                            </div>
                            {currentUser.role === 'admin' && (
                              <p className="text-xs text-gray-500 mt-1">
                                Agent: {agent?.name}
                              </p>
                            )}
                            {client.rdv_notes && (
                              <p className="text-xs text-gray-600 mt-1 bg-gray-100 p-1 rounded">
                                {client.rdv_notes}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => setEditingRdv(client)}
                            className="text-blue-600 hover:text-blue-900 cursor-pointer"
                            title="Modifier le RDV"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-calendar-line text-4xl mb-2 block"></i>
                <p className="text-sm">Aucun RDV aujourd'hui</p>
              </div>
            )}
          </div>
        </div>

        {/* RDV pour la date sélectionnée */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {formatDate(selectedDate)} ({rdvForSelectedDate.length})
            </h2>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {rdvForSelectedDate.length > 0 ? (
              <div className="space-y-3">
                {rdvForSelectedDate
                  .sort((a, b) => (a.rdv_time || '').localeCompare(b.rdv_time || ''))
                  .map(client => {
                    const agent = users.find(u => u.id === client.assigned_agent) || client.assigned_user;
                    const isToday = client.rdv_date === today;
                    return (
                      <div key={client.id} className={`border rounded-lg p-3 ${isToday ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {client.first_name} {client.last_name}
                            </p>
                            <p className="text-sm text-gray-600">{client.phone}</p>
                            <div className="flex items-center mt-2">
                              <i className={`ri-time-line mr-1 ${isToday ? 'text-blue-600' : 'text-gray-600'}`}></i>
                              <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-600'}`}>
                                {formatTime(client.rdv_time || '')}
                              </span>
                            </div>
                            {currentUser.role === 'admin' && (
                              <p className="text-xs text-gray-500 mt-1">
                                Agent: {agent?.name}
                              </p>
                            )}
                            {client.rdv_notes && (
                              <p className="text-xs text-gray-600 mt-1 bg-white p-1 rounded">
                                {client.rdv_notes}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => setEditingRdv(client)}
                            className="text-blue-600 hover:text-blue-900 cursor-pointer"
                            title="Modifier le RDV"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-calendar-line text-4xl mb-2 block"></i>
                <p className="text-sm">Aucun RDV pour cette date</p>
              </div>
            )}
          </div>
        </div>

        {/* Prochains RDV */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Prochains RDV ({upcomingRdv.length})
              </h2>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                <span className="text-xs text-green-600">7 jours</span>
              </div>
            </div>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {upcomingRdv.length > 0 ? (
              <div className="space-y-3">
                {upcomingRdv.map(client => {
                  const agent = users.find(u => u.id === client.assigned_agent) || client.assigned_user;
                  return (
                    <div key={client.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {client.first_name} {client.last_name}
                          </p>
                          <p className="text-sm text-gray-600">{client.phone}</p>
                          <div className="flex items-center mt-2 space-x-4">
                            <div className="flex items-center">
                              <i className="ri-calendar-line text-green-600 mr-1"></i>
                              <span className="text-sm font-medium text-green-600">
                                {new Date(client.rdv_date!).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <i className="ri-time-line text-green-600 mr-1"></i>
                              <span className="text-sm font-medium text-green-600">
                                {formatTime(client.rdv_time || '')}
                              </span>
                            </div>
                          </div>
                          {currentUser.role === 'admin' && (
                            <p className="text-xs text-gray-500 mt-1">
                              Agent: {agent?.name}
                            </p>
                          )}
                          {client.rdv_notes && (
                            <p className="text-xs text-gray-600 mt-1 bg-white p-1 rounded">
                              {client.rdv_notes}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setEditingRdv(client)}
                          className="text-blue-600 hover:text-blue-900 cursor-pointer"
                          title="Modifier le RDV"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-calendar-check-line text-4xl mb-2 block"></i>
                <p className="text-sm">Aucun RDV planifié</p>
                <p className="text-xs">Les prochains RDV apparaîtront ici</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vue planning mensuel */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Vue planning - {new Date(selectedDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-7 gap-2 mb-4">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendrier simple avec indicateurs de RDV */}
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 35 }, (_, index) => {
              const currentMonth = new Date(selectedDate).getMonth();
              const currentYear = new Date(selectedDate).getFullYear();
              const firstDay = new Date(currentYear, currentMonth, 1).getDay();
              const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
              
              const dayNumber = index - (firstDay === 0 ? 6 : firstDay - 1) + 1;
              const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;
              const dayDate = isValidDay ? `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${dayNumber.toString().padStart(2, '0')}` : '';
              
              const dayRdv = isValidDay ? clientsWithRdv.filter(client => client.rdv_date === dayDate) : [];
              const isToday = dayDate === today;
              const isSelected = dayDate === selectedDate;
              
              return (
                <div
                  key={index}
                  className={`min-h-[60px] border rounded-lg p-1 cursor-pointer transition-colors ${
                    !isValidDay 
                      ? 'bg-gray-50' 
                      : isSelected 
                        ? 'bg-blue-100 border-blue-300' 
                        : isToday 
                          ? 'bg-red-50 border-red-200' 
                          : dayRdv.length > 0 
                            ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                            : 'hover:bg-gray-50'
                  }`}
                  onClick={() => isValidDay && setSelectedDate(dayDate)}
                >
                  {isValidDay && (
                    <>
                      <div className={`text-sm font-medium ${isToday ? 'text-red-600' : isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
                        {dayNumber}
                      </div>
                      {dayRdv.length > 0 && (
                        <div className="mt-1">
                          <div className="text-xs bg-green-600 text-white rounded px-1 py-0.5 text-center">
                            {dayRdv.length} RDV
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
